import React from 'react';
import {
    Paper,
    Typography,
    Box,
    Skeleton,
    Stack,
    Avatar,
    LinearProgress,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import useCurrency from '../../hooks/common/useCurrency';
import { formatNumber } from '../../utils/money';

const resolveName = (row, lang) => {
    const n = row.name;
    if (!n) return row.title || row.label || '-';
    if (typeof n === 'string') return n;
    return n[lang] || n.en || n.ar || '-';
};

const resolveQty = (row) =>
    Number(row.qty_sold ?? row.quantity ?? row.qty ?? row.count ?? 0);

const resolveRevenue = (row) =>
    Number(row.revenue ?? row.total ?? row.amount ?? 0);

const resolveImage = (row) =>
    row.image || row.thumbnail || row.cover || row.logo || null;

/**
 * Generic card showing a ranked list of entities (products / categories / brands)
 * with a horizontal progress bar comparing each row's qty against the top row.
 */
const TopEntitiesCard = ({ title, items, isLoading, color = 'primary', emptyText }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const theme = useTheme();
    const { format: formatPrice, locale } = useCurrency();

    const lang = i18n.language || 'en';
    const maxQty = Math.max(0, ...items.map(resolveQty));

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {title}
            </Typography>

            {isLoading ? (
                <Stack spacing={1.5}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
                    ))}
                </Stack>
            ) : items.length === 0 ? (
                <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                        {emptyText || (isArabic ? 'لا توجد بيانات' : 'No data')}
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={1.75}>
                    {items.map((row, idx) => {
                        const qty = resolveQty(row);
                        const revenue = resolveRevenue(row);
                        const name = resolveName(row, lang);
                        const image = resolveImage(row);
                        const pct = maxQty > 0 ? (qty / maxQty) * 100 : 0;

                        return (
                            <Box key={row.id || idx}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ width: 22, textAlign: 'center', flexShrink: 0 }}
                                    >
                                        {formatNumber(idx + 1, locale)}
                                    </Typography>
                                    {image ? (
                                        <Avatar src={image} sx={{ width: 32, height: 32 }} />
                                    ) : (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: `${color}.lighter`, color: `${color}.main`, fontSize: 14 }}>
                                            {String(name).charAt(0).toUpperCase()}
                                        </Avatar>
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap title={name}>
                                            {name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatNumber(qty, locale)} {isArabic ? 'مبيع' : 'sold'}
                                            {revenue > 0 && (
                                                <> · {formatPrice(revenue)}</>
                                            )}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    color={color}
                                    sx={{ mt: 0.75, height: 6, borderRadius: 3 }}
                                />
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );
};

export default TopEntitiesCard;
